import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PageBanner from '../components/PageBanner';
import CategoryProductList from '../components/CategoryProductList';
import { fetchAllProducts, fetchCategories } from '../api/catalog';
import { CATALOG, PRODUCT_CATEGORIES } from '../data/productCatalog';
import {
  getCategoryFallbackImage,
  mapApiProductToWaveItem,
} from '../utils/catalogDisplay';
import { PAGE_BANNER_IMAGES } from '../data/homeData';
import '../Home.css';
import '../PageBanner.css';
import '../ProductDetail.css';

function slugify(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function buildFallbackCategory(categorySlug) {
  const match = PRODUCT_CATEGORIES.find((row) => slugify(row.label) === categorySlug);
  if (!match) return null;

  const products = CATALOG
    .filter((row) => row.category === match.label)
    .map((row, index) => ({
      id: `fallback-${index}-${slugify(row.name)}`,
      name: row.name,
      slug: slugify(row.name),
      description: row.tagline || `Premium ${row.name} for export`,
      image: getCategoryFallbackImage(row.category),
      hsn: row.hsn || null,
      topSelling: false,
    }));

  return {
    category: {
      id: `fallback-${slugify(match.label)}`,
      name: match.label,
      slug: slugify(match.label),
      description: match.subtitle || `Browse our ${match.label} export range.`,
    },
    products,
  };
}

function CategoryProductsPage() {
  const { categorySlug } = useParams();
  const [category, setCategory] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [categorySlug]);

  useEffect(() => {
    let cancelled = false;

    async function loadCategory() {
      setLoading(true);
      setError('');

      try {
        const [categories, productRows] = await Promise.all([
          fetchCategories(),
          fetchAllProducts({ category_slug: categorySlug }),
        ]);

        if (cancelled) return;

        const match = categories.find((row) => row.slug === categorySlug);
        if (!match) {
          setError('Category not found.');
          return;
        }

        setCategory(match);
        setProducts(productRows.map(mapApiProductToWaveItem));
      } catch (err) {
        if (!cancelled) {
          const fallback = buildFallbackCategory(categorySlug);
          if (fallback) {
            setCategory(fallback.category);
            setProducts(fallback.products);
            setError('');
          } else {
            setError(err.message || 'Unable to load category products.');
          }
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadCategory();

    return () => {
      cancelled = true;
    };
  }, [categorySlug]);

  return (
    <div className="page">
      <Header />
      <main className="main products-page-main">
        <PageBanner
          image={PAGE_BANNER_IMAGES.category}
          eyebrow={category ? category.name : 'Category'}
          title={category?.name || 'Loading category…'}
          lead={
            category?.description ||
            (category ? `Browse our full ${category.name} export range for UK wholesale.` : '')
          }
          breadcrumbs={[
            { label: 'Products', to: '/products' },
            { label: category?.name || 'Category' },
          ]}
        />

        <section className="pd-category-body">
          <div className="pd-shell">
            {loading && <p className="product-status">Loading category…</p>}

            {!loading && error && (
              <div className="pd-not-found">
                <h2>Category not found</h2>
                <p>{error}</p>
                <Link to="/products" className="pd-back-link">
                  <ArrowLeft size={16} />
                  Back to all categories
                </Link>
              </div>
            )}
          </div>
        </section>

        {!loading && !error && products.length > 0 && (
          <CategoryProductList
            products={products}
            categoryName={category?.name || ''}
            sectionTitle={`All ${category?.name || ''} products`}
          />
        )}
      </main>
      <Footer />
    </div>
  );
}

export default CategoryProductsPage;
